import React, { useState } from "react";
import { Avatar, Box, Chip, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  describeCheckInCadence,
  describeCoachReviewsSummary,
  describeDurationWeeks,
  describeJourneyWindowFromPublish,
  formatDisplayDate,
  formatReviewPointLabel,
  getJourneyStartLabel,
  getTrackerAccessLabel,
  isTrackerPublicLinkAvailable,
  normalizeJourneyStartsFrom,
  normalizeTrackerAccess,
  isLeadCaptureTracker,
  COACH_PERMISSION_MODES,
  COACH_PERMISSION_OPTIONS,
  normalizeCoachPermissionMode,
  REPLY_WITHIN_DAYS_OPTIONS,
  REVIEW_POINT_VIEW_THRESHOLD,
  SCHEDULED_REVIEWS_OPTIONS,
  syncReviewPointSettings,
  TRACKER_ACCESS,
} from "./trackerStorage";
import {
  coachAvatarColor,
  coachDisplayName,
  coachInitials,
} from "./AddCoachesPopup";
import { getFieldTypeLabel } from "./questions/questionStorage";
import {
  countPeopleForTracker,
  countSubmissionsForTracker,
} from "./submissionStorage";

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const INK = "#0f172a";

const coachPermissionSummary = (coach) => {
  const mode = normalizeCoachPermissionMode(coach?.permissionMode);
  if (mode !== COACH_PERMISSION_MODES.SPECIFIC) return "All permissions";
  const flags = coach?.permissions || {};
  const enabled = COACH_PERMISSION_OPTIONS.filter((item) =>
    Boolean(flags[item.key])
  ).length;
  return `${enabled} of ${COACH_PERMISSION_OPTIONS.length} enabled`;
};

const Section = ({ title, subtitle, action, children }) => (
  <Box
    sx={{
      height: "100%",
      p: { xs: 2, sm: 2.25 },
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      bgcolor: "#fff",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    }}
  >
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      spacing={1.5}
      sx={{ mb: 1.5 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography sx={{ mt: 0.25, fontSize: 12.5, color: MUTED }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action}
    </Stack>
    {children}
  </Box>
);

/** Simple label → value rows — no dense grids. */
const InfoRows = ({ rows }) => (
  <Box>
    {rows
      .filter((row) => row && row.value != null && row.value !== "")
      .map((row) => (
        <Box
          key={row.label}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 0.25, sm: 1.5 },
            py: 1,
            borderBottom: `1px solid #f1f5f9`,
            "&:last-child": { borderBottom: 0 },
          }}
        >
          <Typography
            sx={{
              flex: { sm: "0 0 140px" },
              fontSize: 12.5,
              fontWeight: 600,
              color: MUTED,
            }}
          >
            {row.label}
          </Typography>
          <Typography
            sx={{
              flex: 1,
              fontSize: 13.5,
              fontWeight: 600,
              color: INK,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {row.value}
          </Typography>
        </Box>
      ))}
  </Box>
);

const Empty = ({ children }) => (
  <Typography sx={{ fontSize: 13.5, color: MUTED }}>{children}</Typography>
);

/**
 * Clear, low-grid view of tracker setup/info.
 */
export default function TrackerDetailInfoTab({ tracker }) {
  const navigate = useNavigate();
  const [scheduleExpanded, setScheduleExpanded] = useState(false);

  if (!tracker) return null;

  const isLeadCapture = isLeadCaptureTracker(tracker);
  const setup = tracker.createSetup;
  const coaches = tracker.coaches || [];
  const programmes = tracker.programmes || [];
  const sharedQuestions = tracker.sharedQuestions || [];
  const tags = tracker.tags || [];
  const coachReview = tracker.coachReview || {};
  const coachReviewQuestions = coachReview.questions || [];
  const settings = tracker.settings || {};
  const access = normalizeTrackerAccess(tracker.trackerAccess, tracker);
  const publishDate = setup?.publishDate || tracker.publishDate || null;
  const journeyStartsFrom = normalizeJourneyStartsFrom(
    setup?.journeyStartsFrom || tracker.journeyStartsFrom
  );
  const reviewPoints = syncReviewPointSettings(
    setup || {},
    tracker.reviewPointSettings
  );
  const scheduleNeedsViewAll =
    reviewPoints.length > REVIEW_POINT_VIEW_THRESHOLD;
  const visibleReviewPoints =
    scheduleNeedsViewAll && !scheduleExpanded
      ? reviewPoints.slice(0, REVIEW_POINT_VIEW_THRESHOLD)
      : reviewPoints;

  const peopleCount = countPeopleForTracker(tracker.id);
  const submissionCount = countSubmissionsForTracker(tracker.id);
  const editHref = `/trackers/edit/${tracker.id}`;

  const scheduledReviewsLabel =
    SCHEDULED_REVIEWS_OPTIONS.find(
      (item) => item.value === (setup?.scheduledReviews || "none")
    )?.label || "—";
  const replyWithinLabel =
    REPLY_WITHIN_DAYS_OPTIONS.find(
      (item) => item.value === Number(setup?.replyWithinDays)
    )?.label || null;

  const summaryBits = isLeadCapture
    ? [
        "Lead Capture",
        getTrackerAccessLabel(tracker.trackerAccess, tracker),
        `${sharedQuestions.length} extra question${
          sharedQuestions.length === 1 ? "" : "s"
        }`,
        `${coaches.length} coach${coaches.length === 1 ? "" : "es"}`,
        `${peopleCount} people`,
      ].filter(Boolean)
    : [
        getTrackerAccessLabel(tracker.trackerAccess, tracker),
        setup
          ? describeDurationWeeks(setup.durationWeeks, setup.customDurationDays)
          : null,
        `${coaches.length} coach${coaches.length === 1 ? "" : "es"}`,
        `${sharedQuestions.length} question${
          sharedQuestions.length === 1 ? "" : "s"
        }`,
        `${peopleCount} people`,
      ].filter(Boolean);

  return (
    <Box>
      <Box
        sx={{
          width: "100%",
          mb: 2,
          p: { xs: 2, sm: 2.25 },
          borderRadius: "12px",
          border: `1px solid ${BORDER}`,
          bgcolor: "#fff",
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "flex-start" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: INK }}>
              {tracker.name ||
                (isLeadCapture ? "Untitled lead capture" : "Untitled journey")}
            </Typography>
            <Typography
              sx={{ mt: 0.5, fontSize: 13, color: MUTED, lineHeight: 1.5 }}
            >
              {summaryBits.join(" · ")}
            </Typography>
          </Box>
          <button
            type="button"
            className="small-contained-outlined-button"
            onClick={() => navigate(editHref)}
          >
            Edit tracker
          </button>
        </Stack>
      </Box>

      <div className="row">
        <div className="col-12 col-lg-6 mt-3">
          <Section title="Basics" subtitle="Who can join and when it opens">
            <InfoRows
              rows={[
                {
                  label: "Access",
                  value: getTrackerAccessLabel(tracker.trackerAccess, tracker),
                },
                {
                  label: "Publish date",
                  value: publishDate
                    ? formatDisplayDate(publishDate) ||
                      moment(publishDate).format("D MMM YYYY")
                    : "—",
                },
                !isLeadCapture
                  ? {
                      label: "Journey starts",
                      value: getJourneyStartLabel(journeyStartsFrom),
                    }
                  : {
                      label: "Type",
                      value: "Lead Capture · Standard",
                    },
                access === TRACKER_ACCESS.GYMRITE_MEMBERS
                  ? {
                      label: "Programme",
                      value: programmes.length
                        ? programmes
                            .map((item) => item.title || item)
                            .join(", ")
                        : "None selected",
                    }
                  : {
                      label: "Public link",
                      value: isTrackerPublicLinkAvailable(tracker)
                        ? "Available"
                        : "Not available",
                    },
                tracker.description
                  ? { label: "Internal note", value: tracker.description }
                  : null,
                tracker.visitorMessage
                  ? { label: "Visitor message", value: tracker.visitorMessage }
                  : null,
              ]}
            />
          </Section>
        </div>

        <div className="col-12 col-lg-6 mt-3">
          {isLeadCapture ? (
            <Section
              title="Questions"
              subtitle="Extra questions on this lead form"
              action={
                <button
                  type="button"
                  className="small-contained-outlined-button"
                  onClick={() =>
                    navigate(editHref, { state: { builderTab: "questions" } })
                  }
                >
                  Edit
                </button>
              }
            >
              {sharedQuestions.length === 0 ? (
                <Empty>No extra questions added.</Empty>
              ) : (
                <Stack spacing={0}>
                  {sharedQuestions.map((question, index) => {
                    const typeLabel = getFieldTypeLabel(
                      question.fieldType || question.type
                    );
                    return (
                      <Box
                        key={question.id || index}
                        sx={{
                          py: 1.15,
                          borderBottom: `1px solid #f1f5f9`,
                          "&:last-child": { borderBottom: 0 },
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 13.5, fontWeight: 600, color: INK }}
                        >
                          {index + 1}.{" "}
                          {question.label ||
                            question.statement ||
                            "Untitled question"}
                        </Typography>
                        <Typography sx={{ mt: 0.5, fontSize: 12, color: MUTED }}>
                          {question.required ? "Required" : "Optional"}
                          {typeLabel ? ` · ${typeLabel}` : ""}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Section>
          ) : setup ? (
            <Section
              title="Journey length & check-ins"
              subtitle="How long it runs and how often members submit"
            >
              <InfoRows
                rows={[
                  {
                    label: "Duration",
                    value: describeDurationWeeks(
                      setup.durationWeeks,
                      setup.customDurationDays
                    ),
                  },
                  {
                    label: "Check-in cadence",
                    value: describeCheckInCadence(setup),
                  },
                  {
                    label: "Journey window",
                    value: describeJourneyWindowFromPublish(setup),
                  },
                  {
                    label: "Coach on every check-in",
                    value: setup.alsoReviewEveryCheckin
                      ? replyWithinLabel
                        ? `Yes · within ${replyWithinLabel}`
                        : "Yes"
                      : "No",
                  },
                ]}
              />
            </Section>
          ) : (
            <Section
              title="Journey length & check-ins"
              subtitle="How long it runs and how often members submit"
            >
              <Empty>Set duration and check-ins in the builder.</Empty>
            </Section>
          )}
        </div>

        {isLeadCapture ? (
        <div className="col-12 col-lg-6 mt-3">
          <Section
            title="Coaches"
            subtitle="Who follows up on these leads"
            action={
              <button
                type="button"
                className="small-contained-outlined-button"
                onClick={() =>
                  navigate(editHref, { state: { builderTab: "questions" } })
                }
              >
                Edit
              </button>
            }
          >
            {coaches.length === 0 ? (
              <Empty>No coaches assigned yet.</Empty>
            ) : (
              <Stack spacing={1.25}>
                {coaches.map((coach) => (
                  <Stack
                    key={coach.id}
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                  >
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        fontSize: 12,
                        fontWeight: 700,
                        bgcolor: coachAvatarColor(coach),
                      }}
                    >
                      {coachInitials(coach)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{ fontSize: 14, fontWeight: 700, color: INK }}
                      >
                        {coachDisplayName(coach)}
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                        {coach.email || "Assigned coach"}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </Section>
        </div>
        ) : (
        <div className="col-12 col-lg-6 mt-3">
          <Section
            title="Coaches"
            subtitle="Who owns follow-up on this journey"
            action={
              <button
                type="button"
                className="small-contained-outlined-button"
                onClick={() =>
                  navigate(editHref, { state: { builderTab: "coaches" } })
                }
              >
                Edit
              </button>
            }
          >
            {coaches.length === 0 ? (
              <Empty>No coaches assigned yet.</Empty>
            ) : (
              <Stack spacing={1.25}>
                {coaches.map((coach) => (
                  <Stack
                    key={coach.id}
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                  >
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        fontSize: 12,
                        fontWeight: 700,
                        bgcolor: coachAvatarColor(coach),
                      }}
                    >
                      {coachInitials(coach)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{ fontSize: 14, fontWeight: 700, color: INK }}
                      >
                        {coachDisplayName(coach)}
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                        {coachPermissionSummary(coach)}
                        {coach.email ? ` · ${coach.email}` : ""}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </Section>
        </div>
        )}

        {!isLeadCapture && (
        <div className="col-12 col-lg-6 mt-3">
          <Section
            title="Coach review"
            subtitle="When coaches are asked to reply"
          >
            {coachReview.enabled === false ? (
              <Empty>Coach review is off for this tracker.</Empty>
            ) : (
              <>
                <InfoRows
                  rows={[
                    { label: "Status", value: "Enabled" },
                    setup
                      ? { label: "Schedule", value: scheduledReviewsLabel }
                      : null,
                    {
                      label: "How it works",
                      value: setup
                        ? describeCoachReviewsSummary(setup).replace(
                            /\. The coach is reminded by the Review reminder\.$/,
                            ""
                          )
                        : "—",
                    },
                    {
                      label: "Review questions",
                      value: `${coachReviewQuestions.length} question${
                        coachReviewQuestions.length === 1 ? "" : "s"
                      }`,
                    },
                  ]}
                />
                {coachReviewQuestions.length > 0 ? (
                  <Box sx={{ mt: 1.5 }}>
                    {coachReviewQuestions.slice(0, 5).map((question, index) => (
                      <Typography
                        key={question.id || index}
                        sx={{
                          fontSize: 13,
                          color: "#334155",
                          py: 0.6,
                          borderBottom: `1px solid #f1f5f9`,
                          "&:last-child": { borderBottom: 0 },
                        }}
                      >
                        {index + 1}.{" "}
                        {question.statement ||
                          question.label ||
                          question.title ||
                          `Question ${index + 1}`}
                      </Typography>
                    ))}
                    {coachReviewQuestions.length > 5 ? (
                      <Typography sx={{ mt: 0.75, fontSize: 12, color: MUTED }}>
                        +{coachReviewQuestions.length - 5} more
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}
              </>
            )}
          </Section>
        </div>
        )}

        {!isLeadCapture && (
        <div className="col-12 col-lg-6 mt-3">
          <Section
            title="Questions"
            subtitle="What people answer on check-ins"
            action={
              <button
                type="button"
                className="small-contained-outlined-button"
                onClick={() =>
                  navigate(editHref, { state: { builderTab: "questions" } })
                }
              >
                Edit
              </button>
            }
          >
            {sharedQuestions.length === 0 ? (
              <Empty>No questions yet.</Empty>
            ) : (
              <Stack spacing={0}>
                {sharedQuestions.map((question, index) => {
                  const statement =
                    question.statement ||
                    question.label ||
                    question.title ||
                    "Untitled question";
                  const typeLabel = getFieldTypeLabel(
                    question.fieldType || question.type
                  );
                  return (
                    <Box
                      key={question.id || index}
                      sx={{
                        py: 1.15,
                        borderBottom: `1px solid #f1f5f9`,
                        "&:last-child": { borderBottom: 0 },
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 13.5, fontWeight: 600, color: INK }}
                      >
                        {index + 1}. {statement}
                      </Typography>
                      <Typography sx={{ mt: 0.5, fontSize: 12, color: MUTED }}>
                        {question.measurable ? "Measurable" : "General"}
                        {typeLabel ? ` · ${typeLabel}` : ""}
                        {question.required ? " · Required" : ""}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Section>
        </div>
        )}

        {!isLeadCapture && (
        <div className="col-12 col-lg-6 mt-3">
          <Section
            title="Submission schedule"
            subtitle="Checkpoints along the journey"
          >
            {reviewPoints.length === 0 ? (
              <Empty>
                No schedule points yet. Set duration on Basics first.
              </Empty>
            ) : (
              <>
                <Stack spacing={0}>
                  {visibleReviewPoints.map((point, index) => {
                    const extraCount = (point.extraQuestions || []).length;
                    const excludeCount = (
                      point.excludedSharedQuestionIds || []
                    ).length;
                    return (
                      <Box
                        key={point.key || point.id}
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          py: 1.1,
                          borderBottom: `1px solid #f1f5f9`,
                          "&:last-child": { borderBottom: 0 },
                        }}
                      >
                        <Typography
                          sx={{
                            width: 22,
                            fontSize: 12,
                            fontWeight: 800,
                            color: PRIMARY,
                            pt: 0.15,
                          }}
                        >
                          {index + 1}
                        </Typography>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 13.5,
                              fontWeight: 600,
                              color: INK,
                            }}
                          >
                            {formatReviewPointLabel(point)}
                          </Typography>
                          <Typography
                            sx={{ fontSize: 12, color: MUTED, mt: 0.2 }}
                          >
                            {extraCount} extra question
                            {extraCount === 1 ? "" : "s"}
                            {excludeCount
                              ? ` · ${excludeCount} excluded`
                              : ""}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
                {scheduleNeedsViewAll ? (
                  <button
                    type="button"
                    className="small-contained-outlined-button"
                    onClick={() => setScheduleExpanded((v) => !v)}
                    style={{ marginTop: 12 }}
                  >
                    {scheduleExpanded
                      ? "Show fewer"
                      : `View all ${reviewPoints.length} points`}
                  </button>
                ) : null}
              </>
            )}
          </Section>
        </div>
        )}

        <div className="col-12 col-lg-6 mt-3">
          <Section
            title="Publish & intake"
            subtitle="How submissions are accepted"
          >
            <InfoRows
              rows={[
                {
                  label: "Accepting submissions",
                  value:
                    settings.acceptingSubmissions === false ? "No" : "Yes",
                },
                {
                  label: "Submission limit",
                  value: settings.submissionLimit
                    ? String(settings.submissionLimit)
                    : "Unlimited",
                },
                {
                  label: "Password",
                  value: settings.requirePassword ? "On" : "Off",
                },
                !isLeadCapture
                  ? {
                      label: "GymTV",
                      value: tracker.gymTv?.enabled ? "On" : "Off",
                    }
                  : null,
                settings.confirmationMessage
                  ? {
                      label: "Confirmation",
                      value: settings.confirmationMessage,
                    }
                  : null,
              ]}
            />
          </Section>
        </div>

        <div className="col-12 col-lg-6 mt-3">
          <Section title="Summary" subtitle="Quick counts for this tracker">
            <InfoRows
              rows={[
                { label: "People", value: String(peopleCount) },
                { label: "Submissions", value: String(submissionCount) },
                {
                  label: "Statuses",
                  value: String((tracker.statuses || []).length),
                },
                { label: "Tags", value: String(tags.length) },
                {
                  label: "Created",
                  value: tracker.createdAt
                    ? moment(tracker.createdAt).format("D MMM YYYY")
                    : "—",
                },
                {
                  label: "Updated",
                  value: tracker.updatedAt
                    ? moment(tracker.updatedAt).format("D MMM YYYY HH:mm")
                    : "—",
                },
              ]}
            />
            {tags.length > 0 ? (
              <Stack
                direction="row"
                spacing={0.75}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 1.5 }}
              >
                {tags.map((tag) => (
                  <Chip
                    key={typeof tag === "string" ? tag : tag.id || tag.title}
                    label={typeof tag === "string" ? tag : tag.title || tag}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      bgcolor: PRIMARY_SOFT,
                      color: PRIMARY,
                    }}
                  />
                ))}
              </Stack>
            ) : null}
          </Section>
        </div>
      </div>
    </Box>
  );
}
